import LegalPageLayout from "@/components/legal/LegalPageLayout";

const Privacy = () => {
  return (
    <LegalPageLayout title="Privacy Policy" effectiveDate="April 30, 2026" version="1.2">
      <h2>1. Introduction</h2>
      <p>
        This Privacy Policy explains how Young's Digital Atelier LLC, a subsidiary of Young's
        Multimedia Holdings LLC ("Wavlynq," "we," "us," or "our"), collects, uses, shares, and
        protects information when you use the Wavlynq portal and any of its sibling apps —
        currently Fryqnc, Pulsynq, and MindLynq, with Spiritsynq, Jadesynq, Deafwav, Ablelynq,
        Fullynq, and Openlynq launching next (collectively, the "Services"). By using the Services
        you agree to this Policy.
      </p>
      <p>
        Wavlynq is intended for users who are <strong>18 years of age or older</strong>. We do not
        knowingly collect personal information from anyone under 18.
      </p>

      <h2>2. Information We Collect</h2>
      <h3>2.1 Information You Provide</h3>
      <ul>
        <li><strong>Account information:</strong> email address, password (hashed by our auth provider), preferred name, legal name, date of birth, phone number, pronouns, and the sibling app(s) you join.</li>
        <li><strong>Profile information:</strong> photos, bio, interests, communication preferences, location preferences, mentor status, and any other information you choose to add to your profile.</li>
        <li><strong>Communications:</strong> direct messages, voice notes, community posts, event RSVPs, and support requests.</li>
        <li><strong>Payment information:</strong> processed by Stripe; we do not store full card numbers.</li>
        <li><strong>Identity verification (KYC):</strong> selfie photos and government-issued ID submitted to <strong>Stripe Identity</strong> (or Veriff, Onfido, or Persona as fallback). We receive and store the verification result and limited metadata; raw ID images are held by the verification provider under their own retention rules.</li>
        <li><strong>Background-check data:</strong> only if you opt in to a background-check add-on or apply to become a Mentor.</li>
        <li><strong>Consents:</strong> timestamped records of your acceptance of this Policy, our Terms, age attestation, and any optional opt-ins (SMS, marketing email, AI Training Contribution).</li>
      </ul>

      <h3>2.2 Information Collected Automatically</h3>
      <ul>
        <li><strong>Device & log data:</strong> IP address, browser/user agent, device identifiers, crash logs, timestamps.</li>
        <li><strong>Usage data:</strong> features used, screens viewed, matches, swipes, message metadata (not content) for spam and abuse detection.</li>
        <li>
          <strong>Approximate and precise location:</strong> we derive approximate location from IP
          address. With your permission we use precise location for distance-based discovery and for
          map features powered by <strong>Mapbox</strong>; Mapbox receives the coordinates necessary
          to render maps and search for places, under its own privacy terms.
        </li>
        <li><strong>Cookies and pixels:</strong> see our <a href="/cookies">Cookie Policy</a>.</li>
      </ul>

      <h3>2.3 Information from Third Parties</h3>
      <ul>
        <li><strong>Google or Apple sign-in:</strong> if you sign in with Google or Apple, we receive your email, name, and the provider account ID. Apple may relay a private email address; we honor that.</li>
        <li><strong>Phone sign-in:</strong> if you sign in by phone, we receive a one-time verification code event from our SMS provider.</li>
        <li><strong>Stripe Identity / fallback KYC providers:</strong> verification result, document type, country, and limited metadata.</li>
        <li><strong>Stripe (payments):</strong> subscription status, payment method type, billing country, and Stripe Connect payout details for hosts.</li>
        <li><strong>Background-check provider:</strong> pass/fail result for the optional Background Check Plus add-on or mandatory mentor background check.</li>
      </ul>

      <h2>3. How We Use Information</h2>
      <ul>
        <li>Create and operate your account and provide the Services.</li>
        <li>Match you with compatible members within the sibling app(s) you have joined.</li>
        <li>Send you account, safety, billing, and service messages by email and (where you have opted in) SMS. Auth and safety email is delivered through our per-app branded mailer (powered by Resend).</li>
        <li>Detect, prevent, and respond to fraud, abuse, harassment, and illegal activity, including operating the <strong>Emergency Button</strong> and crisis-aware safety classifiers.</li>
        <li>Comply with legal obligations and resolve disputes.</li>
        <li>Improve the Services, including measuring feature performance.</li>
        <li>With your consent, send you optional marketing about Wavlynq features.</li>
      </ul>

      <h2>4. Per-App Identity and Deadname Protection</h2>
      <p>
        Each sibling app operates a separate identity surface. <strong>A profile you create on one
        sibling app is not visible to members on any other sibling app</strong>, and vice versa.
        Cross-app visibility is only available through the <strong>Attuned</strong> tier and is
        opt-in.
      </p>
      <p>
        We separately store your <strong>preferred name</strong> (the name shown to other members
        everywhere in the product) and your <strong>legal name</strong> (used only for legal,
        dispute, billing, and identity-verification purposes). Your legal name and any prior names
        you have provided are <strong>never displayed to other members</strong> and are excluded from
        search and autocomplete.
      </p>

      <h2>5. SMS Messaging</h2>
      <p>
        If you opt in to SMS, we use your mobile phone number to send account-related messages such
        as verification codes (including phone sign-in), login alerts, match notifications, event
        reminders, and billing notices. Message and data rates may apply. Message frequency varies.
        Reply <strong>HELP</strong> for help or <strong>STOP</strong> to unsubscribe at any time.
      </p>
      <p>
        <strong>We do not share mobile phone numbers or SMS opt-in information with third parties or
        affiliates for marketing purposes.</strong>
      </p>

      <h2>6. Emergency Button and Safety Recording</h2>
      <p>
        The Emergency Button is available in every sibling app. When you press it, the app contacts
        the appropriate locale-aware emergency number and, where you have enabled it, may begin a
        short audio recording. A recording started this way can only be stopped by the pressing
        member or by Wavlynq's founder contact (jyoung@youngsmultimedia.com). Recordings are stored
        encrypted and retained only for the period required for safety review and any legal process.
      </p>

      <h2>7. How We Share Information</h2>
      <ul>
        <li><strong>With other members:</strong> your profile information (preferred name, photos, bio, etc.) is visible to members within your app per your privacy settings. Incognito mode hides you from passive discovery and is provided free of charge.</li>
        <li>
          <strong>With service providers:</strong> Lovable Cloud (managed backend / hosting /
          database), Stripe (payments, Customer Portal, Connect, Stripe Identity), Twilio (SMS),
          Google and Apple (sign-in), Resend (transactional and auth email), Mapbox (maps and place
          search), the technology providers behind our RAELA AI assistant, background-check
          providers, and analytics providers. These providers are bound by data-processing
          agreements that prohibit them from using your data to train their own models.
        </li>
        <li><strong>For legal reasons:</strong> to comply with law, valid legal process, or to protect rights, safety, or property.</li>
        <li><strong>Business transfers:</strong> in connection with a merger, acquisition, or sale of assets, with notice to you.</li>
        <li><strong>With your consent:</strong> for any other purpose disclosed at the time of collection.</li>
      </ul>
      <p>
        <strong>We do not sell your personal information for money.</strong> We do not engage in
        cross-context behavioral advertising as defined under the CCPA/CPRA.
      </p>

      <h2>8. Your Privacy Rights</h2>
      <h3>8.1 All Users</h3>
      <p>
        You may access, correct, export, or delete your account information at any time from
        Settings, or by emailing <a href="mailto:privacy@wavlynq.com">privacy@wavlynq.com</a>.
      </p>

      <h3>8.2 California Residents (CCPA/CPRA)</h3>
      <p>
        You have the right to: know what personal information we collect, use, and share; delete
        personal information; correct inaccurate information; opt out of "sales" and "sharing" (we do
        not engage in either); limit use of sensitive personal information; and not be discriminated
        against for exercising these rights. To submit a request, email{" "}
        <a href="mailto:privacy@wavlynq.com">privacy@wavlynq.com</a>. We will verify your identity
        before responding.
      </p>

      <h3>8.3 European Economic Area, UK, and Switzerland (GDPR/UK GDPR)</h3>
      <p>
        You have the rights to access, rectification, erasure, restriction, portability, and
        objection, and the right to withdraw consent at any time. Our legal bases are:
        (a) performance of a contract; (b) legitimate interests in safety, fraud prevention, and
        service improvement; (c) consent where required (SMS, marketing, precise location, AI
        Training Contribution); and (d) legal obligations. International transfers from the EEA/UK/CH
        to the US rely on Standard Contractual Clauses and supplementary safeguards. You may lodge a
        complaint with your local data-protection authority. Email{" "}
        <a href="mailto:privacy@wavlynq.com">privacy@wavlynq.com</a>.
      </p>

      <h3>8.4 Other US States</h3>
      <p>
        Residents of states with comprehensive privacy laws (including Virginia, Colorado,
        Connecticut, Utah, and others) have rights similar to those listed above. Submit requests to{" "}
        <a href="mailto:privacy@wavlynq.com">privacy@wavlynq.com</a>.
      </p>

      <h2>9. Data Retention</h2>
      <p>
        We retain account information for as long as your account is active. After deletion, we
        retain a minimal record (account ID, deletion timestamp, basic dispute log) for up to seven
        (7) years to comply with legal, tax, and dispute-resolution obligations. KYC results,
        background-check records, and consent records are retained for the period required by law.
      </p>

      <h2>10. Security</h2>
      <p>
        We use industry-standard technical and organizational safeguards including encryption in
        transit (TLS), encryption at rest, role-based access control, audit logs, row-level security
        on member data, and per-IP rate limiting and validation on auth-email endpoints. No system is
        perfectly secure; you are responsible for keeping your password safe.
      </p>

      <h2>11. Children</h2>
      <p>
        The Services are for users 18 and older. If we learn that a user is under 18 we will delete
        the account. Report any suspected underage account to{" "}
        <a href="mailto:privacy@wavlynq.com">privacy@wavlynq.com</a>.
      </p>

      <h2>12. Changes to this Policy</h2>
      <p>
        We will post any changes here and update the Effective Date. Material changes will be
        announced in-app or by email at least 14 days before they take effect.
      </p>

      <h2>13. Contact Us</h2>
      <p>
        Young's Digital Atelier LLC<br />
        702 Richland Hills Dr #760866<br />
        San Antonio, TX 78245, USA<br />
        Privacy questions: <a href="mailto:privacy@wavlynq.com">privacy@wavlynq.com</a>
      </p>
    </LegalPageLayout>
  );
};

export default Privacy;
